import React, { Component, RefObject } from 'react';
import { 
  Send, 
  Paperclip, 
  Image as ImageIcon, 
  Video, 
  Smile, 
  X, 
  Reply, 
  MoreVertical,
  Download,
  Check,
  CheckCheck,
  MapPin,
  Forward
} from 'lucide-react';
import styles from './Chat.module.scss';
import { apiService } from '../../services/api';
import ForwardDestinationModal from '../Friends/ForwardDestinationModal';

interface MessageAttachment {
  id: string;
  attachmentType: 'image' | 'video' | 'file' | 'sticker' | 'youtube' | 'facebook' | 'twitter' | 'instagram' | 'link' | 'location';
  fileUrl?: string;
  fileName?: string;
  fileSize?: number;
  mimeType?: string;
  thumbnailUrl?: string;
  linkUrl?: string;
  metadata?: {
    videoId?: string;
    embedUrl?: string;
    postId?: string;
    username?: string;
    tweetId?: string;
    latitude?: number;
    longitude?: number;
    address?: string;
    placeName?: string;
    [key: string]: any;
  };
  createdAt: string;
}

interface Message {
  id: string;
  senderId: string;
  recipientId: string;
  messageText: string;
  replyToMessage?: {
    id: string;
    messageText: string;
    senderId: string;
    senderName: string;
    senderProfilePic?: string;
    createdAt: string;
  };
  isRead: boolean;
  readAt?: string;
  attachments?: MessageAttachment[];
  createdAt: string;
  updatedAt: string;
  sender?: {
    id: string;
    userId: string;
    displayName: string;
    profilePic?: string;
  };
  recipient?: {
    id: string;
    userId: string;
    displayName: string;
    profilePic?: string;
  };
}

interface ChatProps {
  friend: {
    id: string;
    userId: string;
    fullName: string;
    email: string;
    avatar?: string;
    profilePicture?: string;
  };
  currentUserId: string;
  currentUserProfileId: string;
  /** When true, chat fills its parent (e.g. full-screen overlay) instead of floating bottom-right */
  embedded?: boolean;
  onClose: () => void;
  onSendMessage?: (
    recipientId: string, 
    messageText: string, 
    replyToMessageId?: string, 
    attachments?: File[],
    location?: {
      latitude: number;
      longitude: number;
      address?: string;
      placeName?: string;
    }
  ) => Promise<void>;
  onLoadMessages?: (friendId: string, markAsRead?: boolean) => Promise<Message[]>;
  onMarkAsRead?: (friendId: string, messageIds?: string[]) => Promise<void>;
  onNewMessage?: (message: Message) => void; // Callback to add new message directly
  onTyping?: (isTyping: boolean) => void; // Callback to notify typing status
  isFriendTyping?: boolean; // Whether friend is typing
}

interface SelectedFile {
  file: File;
  preview?: string;
}

interface ChatState {
  messages: Message[];
  inputMessage: string;
  showEmojiPicker: boolean;
  showGifPicker: boolean;
  showFilePicker: boolean;
  replyingTo: Message | null;
  selectedFiles: SelectedFile[];
  loading: boolean;
  typing: boolean;
  isTyping: boolean; // Whether user is currently typing
  typingTimeout: NodeJS.Timeout | null; // Timeout to stop typing indicator
  isUploading: boolean;
  location: {
    latitude: number;
    longitude: number;
    address?: string;
    placeName?: string;
  } | null;
  isGettingLocation: boolean;
  forwardMessage: Message | null; // Message selected for forward
}

class Chat extends Component<ChatProps, ChatState> {
  // Expose methods via ref
  public addMessage = (message: Message) => {
    console.log('[Chat] addMessage called with:', message);
    console.log('[Chat] Current messages count:', this.state.messages.length);
    
    // Check if message already exists by ID
    const messageExists = this.state.messages.some(m => m.id === message.id);
    console.log('[Chat] Message already exists by ID:', messageExists);
    
    if (!messageExists) {
      // Check if there's an optimistic message (temp ID) that should be replaced
      // This happens when the real message arrives after the optimistic one
      const optimisticMessageIndex = this.state.messages.findIndex(m => 
        m.id.startsWith('temp-') && 
        m.senderId === message.senderId &&
        m.recipientId === message.recipientId &&
        m.messageText === message.messageText &&
        // Check if messages are within 5 seconds of each other (to match optimistic with real)
        Math.abs(new Date(m.createdAt).getTime() - new Date(message.createdAt).getTime()) < 5000
      );
      
      if (optimisticMessageIndex !== -1) {
        console.log('[Chat] Found optimistic message to replace at index:', optimisticMessageIndex);
        // Replace optimistic message with real message and reset uploading state
        this.setState(prevState => {
          const newMessages = [...prevState.messages];
          newMessages[optimisticMessageIndex] = message;
          return { 
            messages: newMessages,
            isUploading: false // Reset uploading state when real message arrives
          };
        }, () => {
          console.log('[Chat] Optimistic message replaced with real message, new count:', this.state.messages.length);
          // Scroll to bottom when message is updated (within chat container only)
          setTimeout(() => {
            this.scrollToBottom();
          }, 50);
        });
      } else {
        console.log('[Chat] Adding new message to state');
        this.setState(prevState => ({
          messages: [...prevState.messages, message],
          isUploading: false // Reset uploading state for any new message from server
        }), () => {
          console.log('[Chat] Message added, new count:', this.state.messages.length);
          // Scroll to bottom when message is added (within chat container only)
          setTimeout(() => {
            this.scrollToBottom();
          }, 50);
        });
      }
    } else {
      console.log('[Chat] Message already exists, skipping');
      // Even if message exists, reset uploading state to be safe
      if (this.state.isUploading) {
        this.setState({ isUploading: false });
      }
    }
  };

  public updateMessageReadStatus = (messageIds: string[]) => {
    this.setState(prevState => ({
      messages: prevState.messages.map(msg => 
        messageIds.includes(msg.id) 
          ? { ...msg, isRead: true, readAt: new Date().toISOString() }
          : msg
      )
    }));
  };
  private messagesEndRef: RefObject<HTMLDivElement>;
  private fileInputRef: RefObject<HTMLInputElement>;
  private imageInputRef: RefObject<HTMLInputElement>;
  private videoInputRef: RefObject<HTMLInputElement>;
  private chatContainerRef: RefObject<HTMLDivElement>;

  constructor(props: ChatProps) {
    super(props);
    this.messagesEndRef = React.createRef();
    this.fileInputRef = React.createRef();
    this.imageInputRef = React.createRef();
    this.videoInputRef = React.createRef();
    this.chatContainerRef = React.createRef();
    
    this.state = {
      messages: [],
      inputMessage: '',
      showEmojiPicker: false,
      showGifPicker: false,
      showFilePicker: false,
      replyingTo: null,
      selectedFiles: [],
      loading: true,
      typing: false,
      isTyping: false,
      typingTimeout: null,
      isUploading: false,
      location: null,
      isGettingLocation: false,
      forwardMessage: null
    };
  }

  componentDidMount() {
    this.loadMessages();
    // Scroll to bottom when chat opens (within chat container only)
    setTimeout(() => {
      this.scrollToBottom();
    }, 100); // Small delay to ensure messages are rendered
  }

  componentDidUpdate(prevProps: ChatProps, prevState: ChatState) {
    // Scroll to bottom when new messages arrive (within chat container only)
    if (prevState.messages.length !== this.state.messages.length) {
      setTimeout(() => {
        this.scrollToBottom();
      }, 50); // Small delay to ensure DOM is updated
    }
  }

  scrollToBottom = () => {
    // Scroll within the chat container only, not the entire page
    if (this.chatContainerRef.current) {
      // Use scrollTop to scroll within the container
      this.chatContainerRef.current.scrollTop = this.chatContainerRef.current.scrollHeight;
    }
  };

  public loadMessages = async () => {
    try {
      this.setState({ loading: true });
      if (this.props.onLoadMessages) {
        // Pass friend's profile ID (not userId) to load messages
        // Use markAsRead=true when reloading (e.g., from socket events) to mark messages as read
        const messages = await this.props.onLoadMessages(this.props.friend.id, true);
        this.setState({ messages, loading: false });
        
        // Note: When markAsRead=true is used in getConversation API, messages are already marked as read
        // So we don't need to call onMarkAsRead again here
      } else {
        // Use dummy messages for preview
        this.setState({ 
          messages: this.getDummyMessages(),
          loading: false 
        });
      }
    } catch (error) {
      console.error('[Chat] Failed to load messages:', error);
      this.setState({ 
        messages: this.getDummyMessages(),
        loading: false 
      });
    }
  };

  getDummyMessages = (): Message[] => {
    return [
      {
        id: '1',
        senderId: this.props.currentUserProfileId,
        recipientId: this.props.friend.id,
        messageText: 'Hey! How are you?',
        isRead: true,
        readAt: new Date(Date.now() - 3500000).toISOString(),
        attachments: [],
        createdAt: new Date(Date.now() - 3600000).toISOString(),
        updatedAt: new Date(Date.now() - 3600000).toISOString()
      },
      {
        id: '2',
        senderId: this.props.friend.id,
        recipientId: this.props.currentUserProfileId,
        messageText: 'I\'m doing great! Thanks for asking.',
        isRead: false,
        attachments: [],
        createdAt: new Date(Date.now() - 3300000).toISOString(),
        updatedAt: new Date(Date.now() - 3300000).toISOString()
      }
    ];
  };

  handleSendMessage = async () => {
    const { inputMessage, replyingTo, selectedFiles, location } = this.state;
    
    // Message must have text, files, or location
    if (!inputMessage.trim() && selectedFiles.length === 0 && !location) return;

    // Create optimistic message immediately (before API call)
    const tempMessageId = `temp-${Date.now()}`;
    const optimisticAttachments: MessageAttachment[] = [];
    
    // Add file attachments
    selectedFiles.forEach((selectedFile, index) => {
      const file = selectedFile.file;
      const attachmentType = file.type.startsWith('image/') ? 'image' : 
                           file.type.startsWith('video/') ? 'video' : 
                           file.type === 'image/gif' ? 'sticker' : 'file';
      optimisticAttachments.push({
        id: `temp-${Date.now()}-${index}`,
        attachmentType: attachmentType as any,
        fileUrl: selectedFile.preview || '',
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
        createdAt: new Date().toISOString()
      });
    });

    // Add location attachment if present
    if (location) {
      optimisticAttachments.push({
        id: `temp-location-${Date.now()}`,
        attachmentType: 'location',
        linkUrl: `https://www.google.com/maps?q=${location.latitude},${location.longitude}`,
        metadata: {
          latitude: location.latitude,
          longitude: location.longitude,
          address: location.address,
          placeName: location.placeName
        },
        createdAt: new Date().toISOString()
      });
    }

    const optimisticMessage: Message = {
      id: tempMessageId,
      senderId: this.props.currentUserProfileId,
      recipientId: this.props.friend.id,
      messageText: inputMessage || '',
      isRead: false,
      attachments: optimisticAttachments.length > 0 ? optimisticAttachments : undefined,
      replyToMessage: replyingTo ? {
        id: replyingTo.id,
        messageText: replyingTo.messageText,
        senderId: replyingTo.senderId,
        senderName: replyingTo.senderId === this.props.currentUserProfileId ? 'You' : this.props.friend.fullName,
        senderProfilePic: replyingTo.senderId === this.props.currentUserProfileId ? undefined : this.props.friend.profilePicture,
        createdAt: replyingTo.createdAt
      } : undefined,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    // Add optimistic message immediately
    console.log('[Chat] Adding optimistic message immediately:', optimisticMessage);
    this.setState(prevState => ({
      messages: [...prevState.messages, optimisticMessage],
      inputMessage: '',
      replyingTo: null,
      selectedFiles: [],
      location: null,
      isUploading: true
    }), () => {
      console.log('[Chat] Optimistic message added, scrolling to bottom');
      setTimeout(() => {
        this.scrollToBottom();
      }, 50);
    });

    try {
      if (this.props.onSendMessage) {
        // Prepare attachments array from selected files
        const attachments = selectedFiles.length > 0 ? selectedFiles.map(sf => sf.file) : undefined;
        
        // Call with location parameter
        await this.props.onSendMessage(
          this.props.friend.id,
          inputMessage || '',
          replyingTo?.id,
          attachments,
          location || undefined
        );
        
        // Note: Don't reset isUploading here - let it be reset when the real message arrives via socket
        // This ensures the upload indicator stays until the message is confirmed
        // The isUploading will be reset in addMessage when the real message arrives
        
      } else {
        console.log('[Chat] No onSendMessage prop, using optimistic message only');
        // Reset uploading state immediately if no API call
        this.setState({ isUploading: false });
      }
    } catch (error) {
      console.error('[Chat] Failed to send message:', error);
      
      // Remove optimistic message on error and reset uploading state
      this.setState(prevState => ({
        messages: prevState.messages.filter(m => m.id !== tempMessageId),
        isUploading: false
      }));
      
      alert('Failed to send message. Please try again.');
    }
  };

  handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>, type: 'image' | 'video' | 'file') => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    // Maximum 10 files
    const maxFiles = 10;
    const currentFiles = this.state.selectedFiles.length;
    const remainingSlots = maxFiles - currentFiles;
    
    if (files.length > remainingSlots) {
      alert(`You can only select up to ${maxFiles} files. You have ${currentFiles} selected. Please select ${remainingSlots} or fewer files.`);
      return;
    }

    const newFiles: SelectedFile[] = [];
    let filesToProcess = Array.from(files).slice(0, remainingSlots);
    
    // Validate file sizes (max 50MB per file)
    const maxFileSize = 50 * 1024 * 1024; // 50MB
    const oversizedFiles = filesToProcess.filter(f => f.size > maxFileSize);
    if (oversizedFiles.length > 0) {
      alert(`Some files exceed the maximum size of 50MB. Please select smaller files.`);
      filesToProcess = filesToProcess.filter(f => f.size <= maxFileSize);
    }

    if (filesToProcess.length === 0) return;

    // Process files and create previews
    let processedCount = 0;
    const filesWithPreviews: SelectedFile[] = [];
    
    filesToProcess.forEach((file, index) => {
      const selectedFile: SelectedFile = { file };
      filesWithPreviews.push(selectedFile);
      
      if (type === 'image' || type === 'video') {
        const reader = new FileReader();
        reader.onloadend = () => {
          filesWithPreviews[index].preview = reader.result as string;
          processedCount++;
          
          if (processedCount === filesToProcess.length) {
            this.setState(prevState => ({
              selectedFiles: [...prevState.selectedFiles, ...filesWithPreviews]
            }));
          }
        };
        reader.readAsDataURL(file);
      } else {
        processedCount++;
        if (processedCount === filesToProcess.length) {
          this.setState(prevState => ({
            selectedFiles: [...prevState.selectedFiles, ...filesWithPreviews]
          }));
        }
      }
    });

    // Reset input
    if (this.fileInputRef.current) this.fileInputRef.current.value = '';
    if (this.imageInputRef.current) this.imageInputRef.current.value = '';
    if (this.videoInputRef.current) this.videoInputRef.current.value = '';
  };

  handleReply = (message: Message) => {
    this.setState({ replyingTo: message });
  };

  cancelReply = () => {
    this.setState({ replyingTo: null });
  };

  handleForwardClick = (message: Message) => {
    if (message.id.startsWith('temp-')) return;
    this.setState({ forwardMessage: message });
  };

  handleForwardConfirm = async (params: {
    destinationType: 'dm' | 'group';
    destinationFriendId?: string;
    destinationGroupId?: string;
  }) => {
    const { forwardMessage } = this.state;
    if (!forwardMessage) return;
    await apiService.forwardMessage({
      sourceType: 'dm',
      sourceMessageId: forwardMessage.id,
      destinationType: params.destinationType,
      destinationFriendId: params.destinationFriendId,
      destinationGroupId: params.destinationGroupId,
    });
    this.setState({ forwardMessage: null });
  };

  removeFile = (index: number) => {
    this.setState(prevState => ({
      selectedFiles: prevState.selectedFiles.filter((_, i) => i !== index)
    }));
  };

  clearAllFiles = () => {
    this.setState({ selectedFiles: [] });
    if (this.fileInputRef.current) this.fileInputRef.current.value = '';
    if (this.imageInputRef.current) this.imageInputRef.current.value = '';
    if (this.videoInputRef.current) this.videoInputRef.current.value = '';
  };

  handleGetLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser.');
      return;
    }

    this.setState({ isGettingLocation: true });

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        
        // Try to get address using reverse geocoding (optional)
        // For now, just set coordinates
        this.setState({
          location: {
            latitude,
            longitude,
            address: `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`
          },
          isGettingLocation: false
        });
      },
      (error) => {
        console.error('Error getting location:', error);
        alert('Failed to get your location. Please make sure location services are enabled.');
        this.setState({ isGettingLocation: false });
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  removeLocation = () => {
    this.setState({ location: null });
  };

  formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  renderAttachment = (attachment: MessageAttachment, messageId?: string): React.ReactNode => {
    // Check if this is an uploading attachment (temp ID or data URL)
    const isUploading = messageId?.startsWith('temp-') || 
                       (attachment.fileUrl && attachment.fileUrl.startsWith('data:'));
    
    switch (attachment.attachmentType) {
      case 'image':
        // Use fileUrl directly for images (it serves as both full image and thumbnail)
        if (!attachment.fileUrl) return null;
        
        return (
          <div className={styles.messageImage}>
            <div className={styles.imageContainer}>
              <img 
                src={attachment.fileUrl} 
                alt={attachment.fileName || 'Shared image'} 
                loading="lazy"
                style={{ 
                  maxWidth: '200px', 
                  maxHeight: '200px',
                  objectFit: 'cover',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
                onClick={() => {
                  if (attachment.fileUrl && !attachment.fileUrl.startsWith('data:')) {
                    window.open(attachment.fileUrl, '_blank');
                  }
                }}
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                }}
              />
              {isUploading && (
                <div className={styles.uploadOverlay}>
                  <div className={styles.uploadSpinner}></div>
                  <span className={styles.uploadText}>Uploading...</span>
                </div>
              )}
            </div>
          </div>
        );
      
      case 'video':
        // Use thumbnailUrl if available, fallback to fileUrl
        if (!attachment.fileUrl && !attachment.thumbnailUrl) return null;
        
        return (
          <div className={styles.messageVideo}>
            <div className={styles.videoContainer}>
              {attachment.thumbnailUrl ? (
                // Show thumbnail if available
                <img 
                  src={attachment.thumbnailUrl} 
                  alt={attachment.fileName || 'Video thumbnail'}
                  style={{ 
                    maxWidth: '200px', 
                    maxHeight: '200px',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    if (attachment.fileUrl) {
                      window.open(attachment.fileUrl, '_blank');
                    }
                  }}
                  onError={(e) => {
                    // Fallback to video player if thumbnail fails
                    const container = e.currentTarget.parentElement;
                    if (container && attachment.fileUrl) {
                      const video = document.createElement('video');
                      video.src = attachment.fileUrl;
                      video.controls = true;
                      video.style.maxWidth = '200px';
                      video.style.maxHeight = '200px';
                      container.innerHTML = '';
                      container.appendChild(video);
                    }
                  }}
                />
              ) : (
                // Fallback to video player if no thumbnail
                <video 
                  src={attachment.fileUrl} 
                  controls={!isUploading}
                  preload="metadata"
                  playsInline
                  muted={false}
                  crossOrigin="anonymous"
                  style={{ maxWidth: '200px', maxHeight: '200px' }}
                  onLoadedMetadata={(e) => {
                    const video = e.currentTarget;
                    video.muted = false;
                    video.volume = 1.0;
                  }}
                  onPlay={(e) => {
                    const video = e.currentTarget;
                    video.muted = false;
                    video.volume = 1.0;
                  }}
                >
                  Your browser does not support the video tag.
                </video>
              )}
              {isUploading && (
                <div className={styles.uploadOverlay}>
                  <div className={styles.uploadSpinner}></div>
                  <span className={styles.uploadText}>Uploading...</span>
                </div>
              )}
              {attachment.fileName && (
                <p style={{ fontSize: '12px', marginTop: '4px', color: 'inherit' }}>
                  {attachment.fileName}
                </p>
              )}
            </div>
          </div>
        );
      
      case 'file':
      case 'sticker':
        return (
          <div className={styles.messageFile}>
            <Paperclip size={16} />
            <div className={styles.fileInfo}>
              <span className={styles.fileName}>{attachment.fileName || 'File'}</span>
              {attachment.fileSize && (
                <span className={styles.fileSize}>
                  {(attachment.fileSize / 1024 / 1024).toFixed(2)} MB
                </span>
              )}
              {attachment.fileUrl && (
                <a 
                  href={attachment.fileUrl} 
                  download={attachment.fileName}
                  className={styles.downloadButton}
                  title="Download file"
                >
                  <Download size={14} />
                </a>
              )}
            </div>
          </div>
        );
      
      case 'youtube':
        const videoId = attachment.metadata?.videoId;
        const embedUrl = attachment.metadata?.embedUrl || `https://www.youtube.com/embed/${videoId}`;
        const thumbnailUrl = attachment.metadata?.thumbnailUrl || attachment.fileUrl;
        
        return (
          <div className={styles.messageYoutube}>
            {videoId ? (
              <div className={styles.youtubeEmbed}>
                <iframe
                  width="100%"
                  height="315"
                  src={embedUrl}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title="YouTube video"
                />
              </div>
            ) : (
              <a 
                href={attachment.linkUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className={styles.youtubeLink}
              >
                {thumbnailUrl && (
                  <img src={thumbnailUrl} alt="YouTube thumbnail" />
                )}
                <div className={styles.youtubeOverlay}>
                  <div className={styles.youtubePlayButton}>▶</div>
                </div>
                <span>Watch on YouTube</span>
              </a>
            )}
          </div>
        );
      
      case 'facebook':
      case 'twitter':
      case 'instagram':
        const platformName = attachment.attachmentType.charAt(0).toUpperCase() + attachment.attachmentType.slice(1);
        return (
          <div className={styles.messageSocial}>
            <a 
              href={attachment.linkUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className={styles.socialLink}
            >
              <span className={styles.socialIcon}>
                {attachment.attachmentType === 'facebook' && '📘'}
                {attachment.attachmentType === 'twitter' && '🐦'}
                {attachment.attachmentType === 'instagram' && '📷'}
              </span>
              <span>Open {platformName} {attachment.attachmentType === 'twitter' ? 'Tweet' : 'Post'}</span>
            </a>
          </div>
        );
      
      case 'link':
        return (
          <div className={styles.messageLink}>
            <a 
              href={attachment.linkUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className={styles.linkPreview}
            >
              <span className={styles.linkIcon}>🔗</span>
              <span className={styles.linkUrl}>{attachment.linkUrl}</span>
            </a>
          </div>
        );
      
      case 'location':
        const { latitude, longitude, address, placeName } = attachment.metadata || {};
        const mapsUrl = attachment.linkUrl || `https://www.google.com/maps?q=${latitude},${longitude}`;
        
        return (
          <div className={styles.messageLocation}>
            <a 
              href={mapsUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className={styles.locationLink}
            >
              <MapPin size={20} />
              <div className={styles.locationInfo}>
                <span className={styles.locationName}>
                  {placeName || address || 'Shared Location'}
                </span>
                {address && placeName && address !== placeName && (
                  <span className={styles.locationAddress}>{address}</span>
                )}
                <span className={styles.locationAction}>Open in Google Maps</span>
              </div>
            </a>
            {latitude && longitude && (
              <div className={styles.locationMap}>
                <iframe
                  width="100%"
                  height="200"
                  frameBorder="0"
                  style={{ border: 0, borderRadius: '8px' }}
                  src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6d-s6L4w&q=${latitude},${longitude}`}
                  allowFullScreen
                  title="Location map"
                />
              </div>
            )}
          </div>
        );
      
      default:
        return null;
    }
  };

  render() {
    const { friend, onClose, embedded } = this.props;
    const { 
      messages, 
      inputMessage, 
      showEmojiPicker, 
      replyingTo, 
      selectedFiles,
      location,
      isGettingLocation,
      isUploading,
      loading 
    } = this.state;

    return (
      <div className={`${styles.chatContainer} ${embedded ? styles.chatContainerEmbedded : ''}`}>
        <div className={styles.chatHeader}>
          <div className={styles.chatHeaderInfo}>
            <img
              src={friend.avatar || friend.profilePicture || 'https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y&s=200'}
              alt={friend.fullName}
              className={styles.chatAvatar}
            />
            <div className={styles.chatUserInfo}>
              <h3>{friend.fullName}</h3>
              <span className={styles.chatStatus}>Online</span>
            </div>
          </div>
          <div className={styles.chatHeaderActions}>
            <button className={styles.chatActionButton} title="More options">
              <MoreVertical size={20} />
            </button>
            <button className={styles.chatActionButton} onClick={onClose} title="Close">
              <X size={20} />
            </button>
          </div>
        </div>

        <div className={styles.chatMessages} ref={this.chatContainerRef}>
          {loading ? (
            <div className={styles.loadingContainer}>
              <div className={styles.spinner}></div>
              <p>Loading messages...</p>
            </div>
          ) : messages.length === 0 ? (
            <div className={styles.emptyChat}>
              <p>No messages yet. Start the conversation!</p>
            </div>
          ) : (
            messages.map((message) => {
              // Compare senderId (profile ID) with currentUserProfileId (profile ID), not currentUserId (user ID)
              const isOwnMessage = message.senderId === this.props.currentUserProfileId;
              return (
                <div
                  key={message.id}
                  data-message-id={message.id}
                  className={`${styles.messageWrapper} ${isOwnMessage ? styles.ownMessage : styles.otherMessage}`}
                >
                  {!isOwnMessage && (
                    <img
                      src={friend.avatar || friend.profilePicture || 'https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y&s=200'}
                      alt={friend.fullName}
                      className={styles.messageAvatar}
                    />
                  )}
                  <div className={styles.messageBubble}>
                    {message.replyToMessage && (
                      <div className={styles.replyPreview} onClick={() => {
                        // Scroll to the original message within chat container only
                        const originalMessage = document.querySelector(`[data-message-id="${message.replyToMessage?.id}"]`);
                        if (originalMessage && this.chatContainerRef.current) {
                          // Scroll within the chat container, not the entire page
                          const container = this.chatContainerRef.current;
                          const messageElement = originalMessage as HTMLElement;
                          const containerRect = container.getBoundingClientRect();
                          const messageRect = messageElement.getBoundingClientRect();
                          const scrollTop = container.scrollTop + (messageRect.top - containerRect.top) - (container.clientHeight / 2) + (messageRect.height / 2);
                          container.scrollTo({ top: scrollTop, behavior: 'smooth' });
                          
                          // Highlight the message briefly
                          messageElement.classList.add(styles.highlightMessage);
                          setTimeout(() => {
                            messageElement.classList.remove(styles.highlightMessage);
                          }, 2000);
                        }
                      }}>
                        <div className={styles.replyLine}></div>
                        <div className={styles.replyContent}>
                          <div className={styles.replyHeader}>
                            {message.replyToMessage.senderProfilePic && (
                              <img 
                                src={message.replyToMessage.senderProfilePic} 
                                alt={message.replyToMessage.senderName}
                                className={styles.replyAvatar}
                              />
                            )}
                            <span className={styles.replySender}>{message.replyToMessage.senderName}</span>
                          </div>
                          <span className={styles.replyText}>{message.replyToMessage.messageText}</span>
                        </div>
                      </div>
                    )}
                    
                    {/* Render attachments */}
                    {message.attachments && message.attachments.length > 0 && (
                      <div className={styles.messageAttachments}>
                        {message.attachments.map((attachment) => (
                          <div key={attachment.id} className={styles.attachment}>
                            {this.renderAttachment(attachment, message.id)}
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {message.messageText && (
                      <p className={styles.messageText}>{message.messageText}</p>
                    )}
                    
                    <div className={styles.messageFooter}>
                      <span className={styles.messageTime}>{this.formatTime(message.createdAt)}</span>
                      {isOwnMessage && (
                        <span className={styles.messageStatus}>
                          {message.isRead && message.readAt ? (
                            <CheckCheck size={14} className={styles.readIcon} />
                          ) : (
                            <Check size={14} className={styles.sentIcon} />
                          )}
                        </span>
                      )}
                    </div>
                    
                    <div className={styles.messageActionButtons}>
                      {!isOwnMessage && (
                        <button
                          className={styles.replyButton}
                          onClick={() => this.handleReply(message)}
                          title="Reply"
                        >
                          <Reply size={14} />
                        </button>
                      )}
                      {!message.id.startsWith('temp-') && (
                        <button
                          className={styles.replyButton}
                          onClick={() => this.handleForwardClick(message)}
                          title="Forward"
                        >
                          <Forward size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
          
          {/* Typing Indicator */}
          {this.props.isFriendTyping && (
            <div className={styles.typingIndicator}>
              <div className={styles.typingDots}>
                <span></span>
                <span></span>
                <span></span>
              </div>
              <span className={styles.typingText}>{friend.fullName} is typing...</span>
            </div>
          )}
          
          <div ref={this.messagesEndRef} />
        </div>

        {replyingTo && (
          <div className={styles.replyBar}>
            <div className={styles.replyBarContent}>
              <div className={styles.replyBarInfo}>
                <span className={styles.replyBarLabel}>Replying to {replyingTo.senderId === this.props.currentUserProfileId ? 'yourself' : friend.fullName}</span>
                <span className={styles.replyBarText}>{replyingTo.messageText}</span>
              </div>
              <button className={styles.replyBarCancel} onClick={this.cancelReply}>
                <X size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Selected Files Preview */}
        {selectedFiles.length > 0 && (
          <div className={styles.selectedFilesContainer}>
            <div className={styles.selectedFilesHeader}>
              <span>Selected Files ({selectedFiles.length})</span>
              <button className={styles.clearAllFilesButton} onClick={this.clearAllFiles}>
                Clear All
              </button>
            </div>
            <div className={styles.selectedFilesList}>
              {selectedFiles.map((selectedFile, index) => (
                <div key={index} className={styles.selectedFileItem}>
                  {selectedFile.file.type.startsWith('image/') && selectedFile.preview ? (
                    <img src={selectedFile.preview} alt="Preview" className={styles.selectedFilePreview} />
                  ) : selectedFile.file.type.startsWith('video/') && selectedFile.preview ? (
                    <video src={selectedFile.preview} controls className={styles.selectedFilePreview} />
                  ) : (
                    <div className={styles.selectedFileIcon}>
                      <Paperclip size={20} />
                    </div>
                  )}
                  <div className={styles.selectedFileInfo}>
                    <span className={styles.selectedFileName}>{selectedFile.file.name}</span>
                    <span className={styles.selectedFileSize}>
                      {(selectedFile.file.size / 1024 / 1024).toFixed(2)} MB
                    </span>
                  </div>
                  <button 
                    className={styles.removeFileButton} 
                    onClick={() => this.removeFile(index)}
                    title="Remove file"
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Location Preview */}
        {location && (
          <div className={styles.locationPreview}>
            <div className={styles.locationPreviewContent}>
              <MapPin size={20} />
              <div className={styles.locationPreviewInfo}>
                <span className={styles.locationPreviewName}>
                  {location.placeName || location.address || 'Your Location'}
                </span>
                {location.address && location.placeName && location.address !== location.placeName && (
                  <span className={styles.locationPreviewAddress}>{location.address}</span>
                )}
              </div>
              <button 
                className={styles.locationPreviewRemove} 
                onClick={this.removeLocation}
                title="Remove location"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        )}

        <div className={styles.chatInput}>
          <div className={styles.inputActions}>
            <input
              ref={this.fileInputRef}
              type="file"
              accept="*/*"
              style={{ display: 'none' }}
              onChange={(e) => this.handleFileSelect(e, 'file')}
            />
            <input
              ref={this.imageInputRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => this.handleFileSelect(e, 'image')}
            />
            <input
              ref={this.videoInputRef}
              type="file"
              accept="video/*"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => this.handleFileSelect(e, 'video')}
            />
            
            <button
              className={styles.inputActionButton}
              onClick={() => this.imageInputRef.current?.click()}
              title="Send image"
            >
              <ImageIcon size={20} />
            </button>
            <button
              className={styles.inputActionButton}
              onClick={() => this.videoInputRef.current?.click()}
              title="Send video"
            >
              <Video size={20} />
            </button>
            <button
              className={styles.inputActionButton}
              onClick={() => this.fileInputRef.current?.click()}
              title="Send file"
            >
              <Paperclip size={20} />
            </button>
            <button
              className={styles.inputActionButton}
              onClick={this.handleGetLocation}
              disabled={isGettingLocation}
              title="Share location"
            >
              {isGettingLocation ? (
                <div className={styles.spinner}></div>
              ) : (
                <MapPin size={20} />
              )}
            </button>
            <button
              className={styles.inputActionButton}
              onClick={() => this.setState({ showEmojiPicker: !showEmojiPicker })}
              title="Emoji"
            >
              <Smile size={20} />
            </button>
          </div>
          
          <div className={styles.inputWrapper}>
            <textarea
              className={styles.messageInput}
              placeholder="Type..."
              value={inputMessage}
              onChange={(e) => {
                this.setState({ inputMessage: e.target.value });
                // Notify parent about typing status
                if (this.props.onTyping) {
                  this.props.onTyping(true);
                  // Clear typing after 3 seconds of inactivity
                  if (this.state.typingTimeout) {
                    clearTimeout(this.state.typingTimeout);
                  }
                  const timeout = setTimeout(() => {
                    if (this.props.onTyping) {
                      this.props.onTyping(false);
                    }
                  }, 3000);
                  this.setState({ typingTimeout: timeout });
                }
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  this.handleSendMessage();
                }
              }}
              rows={1}
            />
          </div>
          
          <button
            className={styles.sendButton}
            onClick={this.handleSendMessage}
            disabled={(!inputMessage.trim() && selectedFiles.length === 0 && !location) || isUploading}
          >
            {isUploading ? (
              <div className={styles.spinner}></div>
            ) : (
              <Send size={20} />
            )}
          </button>
        </div>

        {showEmojiPicker && (
          <div className={styles.emojiPicker}>
            <div className={styles.emojiPickerHeader}>
              <span>Emojis</span>
              <button onClick={() => this.setState({ showEmojiPicker: false })}>
                <X size={16} />
              </button>
            </div>
            <div className={styles.emojiGrid}>
              {['😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣', '😊', '😇', '🙂', '🙃', '😉', '😌', '😍', '🥰', '😘', '😗', '😙', '😚', '😋', '😛', '😝', '😜', '🤪', '🤨', '🧐', '🤓', '😎', '🤩', '🥳', '😏', '😒', '😞', '😔', '😟', '😕', '🙁', '😣', '😖', '😫', '😩', '🥺', '😢', '😭', '😤', '😠', '😡', '🤬', '🤯', '😳', '🥵', '🥶', '😱', '😨', '😰', '😥', '😓', '🤗', '🤔', '🤭', '🤫', '🤥', '😶', '😐', '😑', '😬', '🙄', '😯', '😦', '😧', '😮', '😲', '🥱', '😴', '🤤', '😪', '😵', '🤐', '🥴', '🤢', '🤮', '🤧', '😷', '🤒', '🤕'].map((emoji) => (
                <button
                  key={emoji}
                  className={styles.emojiButton}
                  onClick={() => {
                    this.setState({
                      inputMessage: this.state.inputMessage + emoji,
                      showEmojiPicker: false
                    });
                  }}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
        )}

        {this.state.forwardMessage && (
          <ForwardDestinationModal
            visible={!!this.state.forwardMessage}
            onClose={() => this.setState({ forwardMessage: null })}
            sourceType="dm"
            sourceMessageId={this.state.forwardMessage.id}
            excludeFriendId={this.props.friend.id}
            onForward={this.handleForwardConfirm}
          />
        )}
      </div>
    );
  }
}

export default Chat;

